import { decodeTokenSafely } from "../core/jwt-decode";
import type {
  ConsentContent,
  DataElementSelection,
  FetchNoticeConsentStatusForScopeParams,
  FetchNoticeConsentStatusParams,
  LedgerConsentOverlay,
  NoticeConsentStatusResponse,
  PurposeSelection,
} from "./types";

/**
 * Go-ledger notice `check-consent` read.
 *
 * Post-cutover, consent records live in the Go ledger, so Python's notice
 * validation (which powered the 409 CONSENT_ALREADY_PROVIDED and the
 * `purpose_selections` / `reconsent_required` overlay) reads an empty consent
 * DB. When a `ledgerBaseUrl` is configured, the modal and the inline embed ask
 * the ledger directly instead.
 *
 * Every failure mode (no ledger configured, undecodable token, non-OK
 * response, network/abort/parse error) degrades to `null` so callers fall
 * back to the legacy Python-driven flow.
 */

/**
 * Fetch the principal's current consent status for a notice from the Go
 * ledger (`check-consent`), with org/workspace supplied explicitly. The
 * principal resolves from the bearer token server-side, so the body is empty.
 * Returns null on any failure so the caller can fall back to the legacy flow.
 */
export const fetchNoticeConsentStatusForScope = async ({
  accessToken,
  ledgerBaseUrl,
  organisationUuid,
  workspaceUuid,
  noticeUuid,
  signal,
}: FetchNoticeConsentStatusForScopeParams): Promise<NoticeConsentStatusResponse | null> => {
  if (!accessToken || !ledgerBaseUrl || !organisationUuid || !workspaceUuid || !noticeUuid) {
    return null;
  }

  try {
    const response = await fetch(
      `${ledgerBaseUrl}/public/organisations/${organisationUuid}/workspaces/${workspaceUuid}/notices/${noticeUuid}/check-consent`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: "{}",
        signal,
      }
    );

    if (!response.ok) return null;

    const data = (await response.json()) as
      | NoticeConsentStatusResponse
      | { detail?: NoticeConsentStatusResponse };
    // The ledger returns the response directly; tolerate a {detail} envelope too.
    const body =
      data && typeof data === "object" && "detail" in data && data.detail
        ? data.detail
        : (data as NoticeConsentStatusResponse);

    if (!body || !Array.isArray(body.purposes)) return null;
    return body;
  } catch {
    // Network / abort / parse failure — degrade to the legacy Python flow.
    return null;
  }
};

/**
 * Same read, resolving org/workspace by decoding the JWT (the modal's tokens
 * carry them). Returns null on any failure.
 */
export const fetchNoticeConsentStatus = async ({
  accessToken,
  ledgerBaseUrl,
  noticeUuid,
  signal,
}: FetchNoticeConsentStatusParams): Promise<NoticeConsentStatusResponse | null> => {
  if (!accessToken || !ledgerBaseUrl || !noticeUuid) return null;

  const decodedToken = decodeTokenSafely(accessToken);
  if (!decodedToken) return null;

  const { organisation_uuid: ORG, workspace_uuid: WS } = decodedToken;
  if (!ORG || !WS) return null;

  return fetchNoticeConsentStatusForScope({
    accessToken,
    ledgerBaseUrl,
    organisationUuid: ORG,
    workspaceUuid: WS,
    noticeUuid,
    signal,
  });
};

/**
 * Whether the principal has already fully consented to this notice with no
 * reconsent needed — the ledger equivalent of Python's 409
 * `CONSENT_ALREADY_PROVIDED`. Requires an active consent record with every
 * mandatory purpose still ACTIVE.
 */
export const isNoticeFullyConsented = (status: NoticeConsentStatusResponse): boolean =>
  status.consented && status.all_mandatory_active;

/**
 * Translate the ledger's notice consent status into the
 * `{ purpose_selections, reconsent_required }` shape the modal already
 * consumes, so prior-consent pre-fill and reconsent categorization work off
 * the ledger. Returns null for a first-time principal (no consent record —
 * every purpose INACTIVE), preserving the normal first-time consent flow.
 *
 * `needs_reconsent` is derived from `status` (anything not ACTIVE needs
 * reconsent: expired / withdrawn / declined / inactive). NOTE: notice-version
 * reconsent is NOT covered — the ledger's check-consent has no version
 * awareness yet, so a republished-notice version bump won't re-prompt until
 * that lands on the ledger (tracked fast-follow).
 */
export const buildLedgerConsentOverlay = (
  status: NoticeConsentStatusResponse,
  configPurposes: ConsentContent["detail"]["active_config"]["purposes"]
): LedgerConsentOverlay | null => {
  const byUuid = new Map(status.purposes.map((p) => [p.uuid, p]));
  const hasHistory = status.purposes.some((p) => (p.status || "INACTIVE") !== "INACTIVE");
  if (!hasHistory) return null;

  const purpose_selections: Record<string, PurposeSelection> = {};
  for (const cp of configPurposes) {
    const lp = byUuid.get(cp.uuid);
    const purposeStatus = lp?.status || "INACTIVE";
    const selectedByUuid = new Map((lp?.data_elements || []).map((de) => [de.uuid, de.selected]));
    const data_elements: Record<string, DataElementSelection> = {};
    for (const de of cp.data_elements) {
      data_elements[de.uuid] = {
        selected: selectedByUuid.get(de.uuid) ?? false,
        enabled: de.enabled,
        required: de.required,
      };
    }
    purpose_selections[cp.uuid] = {
      selected: lp?.selected ?? false,
      status: purposeStatus,
      needs_reconsent: purposeStatus !== "ACTIVE",
      data_elements: purposeStatus === "INACTIVE" ? {} : data_elements,
    };
  }

  const reconsent_required = configPurposes.some(
    (cp) => (purpose_selections[cp.uuid]?.status || "INACTIVE") !== "ACTIVE"
  );
  return { reconsent_required, purpose_selections };
};
